import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import {
    CodedProperty,
    createValidationResult,
    FormDialog,
    FormFields,
    FormValidationSummary,
    NamedProperty,
    Stack,
    useNotifications,
    usePerformMultipleActions
} from "sprint-common-web";

import { OrderRequestStatus } from "../../../../../common/types/OrderRequestStatus";
import { useOrdersModuleApi } from "../../../../common/useOrdersModuleApi";
import { OrderRequestListingContext } from "../../context/OrderRequestListingContext";
import { validateDiscardedItems } from "../SendOrderRequestItem";
import { SendOrderRequestSummaryList } from "../SendOrderRequestSummaryList";

interface SendOrderRequestItem {
    id: string;
    content?: string;
    pONumber: string;
    airline?: CodedProperty;
    system?: NamedProperty;
    status: OrderRequestStatus;
    isTrailer?: boolean;
}

interface SendMultipleOrderRequestsToCloudDialogProps {
    selectedItems: SendOrderRequestItem[];
    onClose(): void;
}

export const SendMultipleOrderRequestsToCloudDialog: React.VFC<SendMultipleOrderRequestsToCloudDialogProps> = ({
    selectedItems,
    onClose
}) => {
    const { createCloudOrderFromRequest } = useOrdersModuleApi();

    const { updateOrderRequest } = useContext(OrderRequestListingContext);

    const { success, error: errorNotification } = useNotifications();

    const { formState, handleSubmit } = useForm();
    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<SendOrderRequestItem>({
        isAvailable: isOrderRequestItemSendable,
        selectedItems
    });

    const formHasErrors = createValidationResult([validateDiscardedItems(items, selectedItems.length)]);
    const areValidOrdersEmpty = !items.length;

    const submit = async () => {
        const results = await sendAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            onClose();

            success("Sending to cloud complete");
        } else errorNotification("Something went wrong while sending order requests");
    };

    const sendAllItems = async () => {
        return performAllItems(async (item) => {
            await createCloudOrderFromRequest({ orderRequestId: item.id });

            updateOrderRequest({ id: item.id, status: OrderRequestStatus.CloudProcessing, isModified: true });
        });
    };

    return (
        <Stack>
            <FormDialog
                title={"Send order requests to cloud"}
                formState={formState}
                text={"Are you sure you want to start sending listed order requests?"}
                isBlocking={isActionInProgress}
                showCloseButton={false}
                submit={{
                    onClick: handleSubmit(submit),
                    disabled: areValidOrdersEmpty || isActionInProgress
                }}
                cancel={{
                    onClick: onClose
                }}
                width={700}
                contentHeight={400}
            >
                <FormFields>
                    <FormValidationSummary validationResult={formHasErrors} />
                </FormFields>
                {!!items.length && <SendOrderRequestSummaryList items={items} />}
            </FormDialog>
        </Stack>
    );
};

const isOrderRequestItemSendable = (item: SendOrderRequestItem): boolean => item.status === OrderRequestStatus.Pending;
